import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import preview from '../../assets/icons/preview.svg';
import { clearSurveyState } from '../../redux/survey/survey.slice';
import ReduxService from '../../redux/redux.service';
import {
  useDeleteSurveyMutation,
  useUpdateSurveyMutation,
} from '../../redux/survey/survey.endpoints';
import SurveyMenu from './survey-header-menu';
// import invoice from '../../assets/icons/invoice.svg';
import download from '../../assets/icons/download.svg';
import ellipses from '../../assets/icons/ellipses.svg';
import { SurveyFull, SurveyStatus } from '../../redux/survey/survey.types';
import { SaveStatusEnum } from '../../redux/survey/survey.types';
import { setSaveState } from '../../redux/survey/survey-status.slice';
import { useDispatch, useSelector } from 'react-redux';
import { setSaveStateWithDelay } from '../../redux/survey/survey-status.utils';
import SurveyPreviewModal from "../survey-preview-modal/survey-preview-modal";
import { displaySurveyStatus, computeColor } from '../survey-status/survey-status-utils';
import DeleteSurveyDialog from '../delete-survey-dialog/delete-survey-dialog';
import { formatDisplayDate } from "../../helpers/date.helpers";
import InvisiblyApiService from "../../services/api/invisibly-api.service";
import { apiUrl } from "../../constants/config";
import SurveyShareButton from '../../pages/view-survey/components/share-survey-button.component';
import { getIsUserAuthenticated } from '../../redux/user/user.selectors';
import { RootState } from '../../redux/redux-store';
import useShareSurvey from '../../hooks/useShareSurvey';
import { createShare } from "../../services/surveys/surveys.helpers";
import CreateMediaPlanButton from "../../pages/view-survey/components/create-media-plan-button.component";
import { ViewSurveyMode } from '../../pages/view-survey/view-survey.types';
import CSVUpload from '../csv-upload/csv-upload';
import SurveyBranchingPreview from '../survey-branching-preview/survey-branching-preview';

export interface SurveyHeaderProps {
  survey?: SurveyFull;
  mode: ViewSurveyMode;
}

const SurveyHeader: React.FC<SurveyHeaderProps> = ({ mode, survey }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: RootState) => getIsUserAuthenticated(state));
  const [title, setTitle] = useState<string>('');
  const [surveyStatus, setSurveyStatus] = useState<SurveyStatus>(SurveyStatus.Draft);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const canDownload = survey?.surveyStatus === SurveyStatus.Complete;
  const [updateSurvey] = useUpdateSurveyMutation();
  const [deleteSurvey] = useDeleteSurveyMutation();
  const [inputValue, setInputValue] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const isFromGeneration = searchParams.get('generated') === 'true';
  const isCsvUpload = searchParams.get('csvUpload') === 'true';
  const [previewVisible, setPreviewVisible] = useState(isFromGeneration);
  const inputPlaceholder = "Give it a name...";

  const { handleShare, isLoading: isShareLoading } = useShareSurvey({ surveyId: survey?.extSurveyId, surveyStatus: survey?.surveyStatus });

  useEffect(() => {
    if (survey) {
      if (survey.title != "Untitled") {
        setInputValue(survey.title);
        setTitle(survey.title);
      }
      setSurveyStatus(survey.surveyStatus || SurveyStatus.Draft);
    }
  }, [survey]);

  const handleBackButtonClick = () => {
    ReduxService.Store.dispatch(clearSurveyState());
    navigate(isLoggedIn ? '/surveys' : '/login');
  };

  const handlePreviewClick = () => {
    setPreviewVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    isFromGeneration && setSearchParams({ 'ext-survey-id': survey?.extSurveyId ?? '' });
  };

  const handleInputChange = (e: { target: { value: React.SetStateAction<string>; }; }) => {
    dispatch(setSaveState({ status: SaveStatusEnum.Syncing }));
    setInputValue(e.target.value);
  };

  // eslint-disable-next-line @typescript-eslint/space-before-function-paren
  const handleBlur = async () => {
    if (inputValue.trim() === '') {
      return;
    }
    await updateSurvey({
      title: inputValue.trim(),
      ext_survey_id: survey?.extSurveyId ?? '',
    });
    setSaveStateWithDelay(
      dispatch,
      SaveStatusEnum.JustSynced,
      SaveStatusEnum.Synced,
      5000
    );
  };


  // eslint-disable-next-line @typescript-eslint/space-before-function-paren
  const deleteSurveyCall = async () => {
    try {
      deleteSurvey({ ext_survey_id: survey?.extSurveyId ?? '' });
      setIsMenuOpen(false);
      handleCloseModal();
      handleBackButtonClick();
    } catch (error) {
      console.error('Failed to delete survey:', error);
    }
  };
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const onDuplicateClick = () => {
    // todo survey-header onDuplicateClick
    console.log("survey-header onDuplicateClick");
  };
  const handleMenuOpen = () => {
    setIsMenuOpen((prev) => !prev);
  };
  useEffect(() => {
    const handler = (e: any) => {
      if (!menuRef.current?.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
  });

  const handleViewInvoice = () => {
    // todo survey-header handleViewInvoice
    console.log('survey-header handleViewInvoice');
  };
  const handleDownload = () => {
    if (!canDownload) {
      return;
    }
    const isShare = mode === 'share';
    const id = (isShare ? searchParams.get('share-id') : survey?.extSurveyId) ?? '';
    if (!id) {
      return;
    }
    const url = createShare(`${apiUrl}/b2b/survey/${id}/csv`, isShare, 'b2b');
    const fileName = survey.title.trim().toLowerCase().split(' ').join('_') + '.csv';
    void InvisiblyApiService.invokeDownloadRequest(url, fileName);
  };

  const { bg: statusBgColor, text: statusTextColor } = computeColor(surveyStatus);

  const SurveyStatusComponent = () => (
    <div className="flex items-center min-w-0">
      <div className='mt-1 ml-6 flex items-center min-w-0'>
        <div className={`w-2.5 h-2.5 rounded-full mr-2 text-xs ${statusBgColor}`} />
        <p className={`${statusTextColor} truncate`}>
          {displaySurveyStatus[surveyStatus]}
        </p>
      </div>
      <div className="mt-2 ml-3 text-sm text-gray-401 truncate">
        {formatDisplayDate(survey?.endDate ?? undefined)}
      </div>
    </div>
  );

  function renderTitle(): ReactElement {
    return (
      <div className="flex-1 min-w-0">
        {mode == "create" ? (
          <div className="flex items-center min-w-0 md:my-2">
            <div className="flex-1 min-w-0 overflow-hidden">
              <input
                type="text"
                defaultValue={inputValue}
                placeholder={inputPlaceholder}
                onBlur={handleBlur}
                onChange={handleInputChange}
                disabled={survey?.surveyStatus === SurveyStatus.Review}
                className="w-full rounded-md border-none bg-transparent pl-4 py-2 text-lg focus:outline-none text-ellipsis"
                style={{ fontSize: '1.5rem' }}
              />
            </div>
            <div className='flex items-center mb-1 ml-4 shrink-0'>
              <SurveyStatusComponent />
            </div>
          </div>
        )
          : (
            <div className="flex items-center rounded-md border-none bg-transparent pl-4 py-2 text-lg focus:outline-none min-w-0">
              <div className="truncate text-2xl lg-max:text-xl sm-max:text-lg min-w-0 flex-1">
                {title}
              </div>
              {mode !== 'error' && (
                <div className='flex items-center md:ml-4 shrink-0'>
                  <SurveyStatusComponent />
                </div>
              )}
            </div>
          )}
      </div>
    );
  }

  return (
    <div className="min-h-20 flex flex-wrap w-full items-center justify-between border-b border-gray-201 p-4">
      {/* <div className="flex items-center order-1">
        <BackButton onClick={handleBackButtonClick} />
      </div> */}
      {mode !== 'create' && mode !== 'error' ? (
        <div className="flex items-center justify-end ml-2 order-2 sm:order-3 sm:ml-0">
          { survey && mode === 'view' && (
            <CreateMediaPlanButton
              extSurveyId={survey?.extSurveyId}
              surveyStatus={surveyStatus}
            />
          )}
          {canDownload ? (
            <button
              onClick={handleDownload}
              className="flex size-10 items-center justify-center rounded-3xl bg-white shadow-md"
            >
              <img
                className="h-4"
                src={download}
                alt=""
              />
            </button>
          ) : null}
          {mode === 'view' ? (
            <SurveyShareButton
              surveyId={survey?.extSurveyId}
              surveyStatus={survey?.surveyStatus}
            />
          ) : null}
          {survey ? (
            <button
              onClick={handlePreviewClick}
              className="ml-2 flex items-center rounded-3xl bg-white lg-max:p-2 px-6 py-2 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-501"
            >
              <img
                className="mr-2 lg-max:m-0"
                src={preview}
                alt=""
                draggable={false}
              />
              <span className="lg-max:hidden">
                Preview
              </span>
            </button>
          ) : null}
          <div
            className="mx-2 flex items-center justify-center h-10 w-10 rounded-full bg-white shadow-md"
            ref={menuRef}
          >
            <img
              src={ellipses}
              className="cursor-pointer w-6"
              onClick={handleMenuOpen}
              draggable={false}
            />
            <div className="relative">
              {isMenuOpen && (
                <SurveyMenu
                  viewScreen={true}
                  handleDownload={handleDownload}
                  handleViewInvoice={handleViewInvoice}
                  handlePreviewClick={handlePreviewClick}
                  handleShare={mode === 'view' ? handleShare : undefined}
                  isShareLoading={isShareLoading}
                  onDeleteClick={handleOpenModal}
                  onDuplicateClick={onDuplicateClick}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end sm-max:mt-2 sm:ml-auto order-2 sm:order-3 sm:ml-0">
          {mode === 'create' ? (
            <div className="flex items-center">
              <CSVUpload
                extSurveyId={survey?.extSurveyId}
                initiallyOpen={isCsvUpload}
              />
              <SurveyBranchingPreview
                extSurveyId={survey?.extSurveyId}
              />
              <button
                onClick={handlePreviewClick}
                className="ml-2 flex items-center rounded-3xl bg-white lg-max:p-2 px-6 py-2 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-501"
              >
                <img
                  className="mr-2 lg-max:m-0"
                  src={preview}
                  alt=""
                  draggable={false}
                />
                <span className="lg-max:hidden">
                  Preview Branding
                </span>
              </button>
              <div
                className="mx-2 flex items-center justify-center h-10 w-10 rounded-full bg-white shadow-md"
                ref={menuRef}
              >
                <img
                  src={ellipses}
                  className="cursor-pointer w-6"
                  onClick={handleMenuOpen}
                  draggable={false}
                />
                <div className="relative">
                  {isMenuOpen && (
                    <SurveyMenu
                      viewScreen={false}
                      handleDownload={handleDownload}
                      handleViewInvoice={handleViewInvoice}
                      handlePreviewClick={handlePreviewClick}
                      onDeleteClick={handleOpenModal}
                      onDuplicateClick={onDuplicateClick}
                    />
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
      <div className="flex flex-grow w-full order-3 mt-4 sm:order-2 sm:mt-0 sm:w-auto justify-between items-center">
        {renderTitle()}
      </div>
      <DeleteSurveyDialog
        deleteSurvey={deleteSurveyCall}
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
      />
      {previewVisible && survey && (
        <SurveyPreviewModal
          questions={survey?.questions || []}
          isVisible={previewVisible}
          onHide={handleClosePreview}
          survey={survey}
        />
      )}
    </div>
  );
};

export default SurveyHeader;
